import React from 'react';
import { Button, Table, Tag } from 'antd';
import { FileDoneOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { OrderProduct } from '../../../../types/order';
import { formatCurrencyLocalized } from '../../../../utils/currency';
import { formatDateEuropean } from '../../../../utils/dateUtils';
import { formatLocalizedPercent } from '../../../../utils/numberFormatting';
import { InvoiceCoverageTag } from '../../../invoices/components/InvoiceCoverageSummary';
import { getOrderProductDisplayName } from '../../orderProductDisplay';
import type { OrderStepContext } from '../orderStepContext';
import { Callout, HiddenValue, KeyValueList, LineValuePending, LineValuesFailedNote, StepSection } from '../OrderProcessParts';
import OrderQuantityPreview from '../../components/OrderQuantityPreview';
import { OrderLineNameWithDimensions } from '../OrderProcessTables';
import { ORDER_MARGIN_STAGE_LABEL_KEYS } from '../orderMarginStage';
import { getOrderMarginBreakdown } from '../orderMarginBreakdown';
import { useMarginIncompleteText } from '../OrderProcessHeader';
import { OrderInvoicesStepSection } from '../../sections/OrderInvoiceCoverageSection';
import type { OrderSalesInvoice } from '../../../../types/orderSalesInvoice';
import type { AdvanceInvoiceBalance } from '../../../invoices/api/invoiceWorkflowApi';

/**
 * Faktura (makieta `sFak`): the sales invoice comes from the external
 * program and is imported through the existing path; approval closes the ZK.
 * Analysis per line and tiles read the same sources as the header chip: the
 * WP5 plan (before approval) or the invoice ledger `actual` (after). Bonus /
 * faktoring per line are not implemented (business decision pending).
 */
export default function InvoiceStep({ ctx, onUploadInvoice, salesInvoices = null, salesInvoicesFailed = false, onRetrySalesInvoices, onInvoicesChanged, advanceBalance = null }: {
  ctx: OrderStepContext;
  onUploadInvoice: () => void;
  /** „Faktury sprzedaży” of the ZK (sales document readers); null = not loaded / not visible. */
  salesInvoices?: OrderSalesInvoice[] | null;
  /** The list could not be loaded: the section says so and offers a retry. */
  salesInvoicesFailed?: boolean;
  onRetrySalesInvoices?: () => void;
  /** An invoice was deleted or withdrawn from the list. */
  onInvoicesChanged?: () => void;
  /** Advances and settlement invoices against the ZK value; null without advances. */
  advanceBalance?: AdvanceInvoiceBalance | null;
}) {
  const { t, i18n } = useTranslation(['orders']);
  const { order, caps, margin, marginVisibility, settlement, readOnly } = ctx;
  const money = (value: number | null | undefined, currency = 'PLN') =>
    (value == null ? '—' : formatCurrencyLocalized(value, currency, i18n.language));
  const percent = (value: number | null | undefined) => (value == null ? '—' : formatLocalizedPercent(value, i18n.language));
  const plan = ctx.warehouse?.finance?.plan;
  const finance = ctx.warehouse?.finance;
  const final = margin.stage === 'FINAL' && settlement;
  const coverage = order.invoiceCoverage;
  const invoiced = order.status === 'INVOICED';
  const products = order.productsLoaded ? order.products : [];

  // One source for the tiles (orderMarginBreakdown.ts): the ledger after the
  // invoice, otherwise the WP5 plan; roles without the plan get no computed cost.
  const breakdown = getOrderMarginBreakdown({ stage: margin.stage, settlement, finance });
  const revenue = breakdown ? breakdown.revenue : order.revenue;
  const incompleteText = useMarginIncompleteText(margin);
  const marginValue = (amount: number | null, pct: number | null) => (margin.costState === 'UNKNOWN'
    ? t('orders:process.margin.unknown')
    : [
      marginVisibility.showAmount && amount != null ? money(amount) : null,
      pct != null ? percent(pct) : null,
    ].filter(Boolean).join(' · ') || t('orders:process.margin.unknown'));
  // The tiles add up: with an incomplete cost the plan has no total, but
  // revenue − Σ known costs is the known-part margin of the chip (one rule:
  // orderMarginSummary.getOrderMarginCostState), shown as „niepełny koszt”.
  const tileMargin = breakdown
    ? {
      amount: breakdown.margin ?? (breakdown.source === 'PLAN' && margin.costState === 'PARTIAL' ? margin.amount : null),
      percent: margin.costState === 'KNOWN' ? breakdown.percent : null,
    }
    : { amount: margin.amount, percent: margin.percent };
  // Released WZ documents with their own backend cost (warehouse cost readers without the plan).
  const wzCostList = !breakdown && caps.canViewWarehouseCosts ? ctx.facts.issuedWz : [];

  // Issue dates of the active invoices (same source as the numbers above);
  // the legacy order field only when the coverage carries none.
  const invoiceDates = (coverage?.invoices ?? []).map((invoice) => invoice.issueDate).filter(Boolean).map((date) => formatDateEuropean(date!))
    .filter((date, index, dates) => dates.indexOf(date) === index).join(', ');
  const dueDate = order.customerPaymentDueDate ? dayjs(order.customerPaymentDueDate) : null;
  const daysToPay = dueDate ? dueDate.startOf('day').diff(dayjs().startOf('day'), 'day') : null;
  // Margin per m² only when every line has its m² (otherwise it would mislead).
  const allLinesInM2 = products.length > 0 && products.every((product) => Number(product.quantityM2) > 0);
  const m2 = allLinesInM2 ? products.reduce((sum, product) => sum + Number(product.quantityM2), 0) : 0;

  const lineCell = (product: OrderProduct) => ctx.fifoLines.find((line) => line.id === product.id);
  const planLine = (product: OrderProduct) => plan?.lines.find((line) => line.id === product.id);

  return (
    <div className="zk-col" data-testid="zk-step-INVOICE">
      <div className="zk-grid">
        <StepSection title={t('orders:process.invoice.infoTitle')}>
          <KeyValueList rows={[
            [t('orders:process.invoice.status'), invoiced
              ? <Tag color="green">{t('orders:process.pill.INVOICED')}</Tag>
              : coverage && coverage.state !== 'NONE'
                ? <InvoiceCoverageTag coverage={coverage} />
                : <Tag color="orange">{t('orders:process.invoice.none')}</Tag>],
            [t('orders:process.invoice.number'), coverage?.invoices.map((invoice) => invoice.number).filter(Boolean).join(', ') || order.invoiceNumber || '—'],
            [t('orders:process.invoice.date'), invoiceDates || (order.invoiceDate ? formatDateEuropean(order.invoiceDate) : '—')],
            [t('orders:process.invoice.dueDate'), caps.canViewCustomerPayments && dueDate ? formatDateEuropean(order.customerPaymentDueDate) : '—'],
          ]} />
        </StepSection>
        <StepSection title={t('orders:process.invoice.uploadTitle')} icon={<FileDoneOutlined />}>
          <Callout tone="info" flush>{t('orders:process.invoice.uploadHint')}</Callout>
          {!readOnly && caps.canManageSalesInvoices && !invoiced && (
            <div className="zk-upload-row">
              <Button type="primary" icon={<UploadOutlined />} onClick={onUploadInvoice}>{t(order.hasAdvanceInvoice ? 'orders:process.bar.uploadSettlementInvoice' : 'orders:process.bar.uploadSalesInvoice')}</Button>
            </div>
          )}
          {invoiced && <Callout tone="success">{t('orders:process.invoice.closed')}</Callout>}
        </StepSection>
      </div>

      {/* Decision 2026-10-19: which invoices the ZK has, „Usuń fakturę”, which invoice covers which line. */}
      <OrderInvoicesStepSection title={t('orders:process.invoice.invoicesTitle')} coverage={coverage}
        salesInvoices={salesInvoices} salesInvoicesFailed={salesInvoicesFailed} onRetrySalesInvoices={onRetrySalesInvoices} advanceBalance={advanceBalance}
        onInvoicesChanged={onInvoicesChanged} />

      <StepSection title={t('orders:process.invoice.analysisTitle')} testId="zk-invoice-analysis">
        {marginVisibility.showMargin && ctx.fifoFailed && <LineValuesFailedNote />}
        <Table<OrderProduct>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={products}
          columns={[
            {
              title: t('orders:process.lines.product'),
              key: 'product',
              render: (_v, product) => (
                <OrderLineNameWithDimensions name={getOrderProductDisplayName(product, product.name)} product={product} testId="zk-invoice-line-dimensions" />
              ),
            },
            { title: t('orders:process.lines.quantity'), key: 'quantity', align: 'right', render: (_v, product) => <OrderQuantityPreview line={product} /> },
            {
              title: t('orders:process.invoice.lineRevenue'),
              key: 'revenue',
              align: 'right',
              render: (_v, product) => (caps.canViewSalePrices
                ? <span className="zk-num">{money(product.totalPrice, product.currency || order.currency || 'PLN')}</span>
                : <HiddenValue reason="salePrices" />),
            },
            {
              title: t('orders:process.invoice.lineWzCost'),
              key: 'cost',
              align: 'right',
              render: (_v, product) => (marginVisibility.showCosts
                ? <span className="zk-num">{money(planLine(product)?.issuedCostPLN)}</span>
                : <HiddenValue reason="costs" />),
            },
            {
              title: t('orders:process.invoice.lineMargin'),
              key: 'margin',
              align: 'right',
              render: (_v, product) => {
                if (!marginVisibility.showMargin) return <HiddenValue reason="margin" />;
                const line = lineCell(product);
                if (!line && ctx.fifoLoading) return <LineValuePending />;
                return (
                  <span className="zk-num">
                    {marginVisibility.showCosts && line?.marginPLN != null && <>{money(line.marginPLN)}<br /></>}
                    <span className="zk-table-note">{percent(line?.marginPercent)}</span>
                  </span>
                );
              },
            },
          ]}
        />
        <div className="zk-tiles" data-testid="zk-invoice-tiles" data-source={breakdown?.source ?? 'NONE'}>
          <div className="zk-tile" data-testid="zk-tile-revenue" data-amount={caps.canViewSalePrices && revenue != null ? revenue : undefined}>
            <span className="zk-tile__label">{t('orders:process.invoice.revenue')}</span>
            <span className="zk-tile__value">{caps.canViewSalePrices ? money(revenue) : <HiddenValue reason="salePrices" />}</span>
            <span className="zk-tile__meta">{t(breakdown?.source === 'SETTLEMENT' ? 'orders:process.invoice.fromInvoice' : 'orders:process.invoice.fromOrder')}</span>
          </div>
          {breakdown ? breakdown.costs.map((cost) => (
            <div className="zk-tile" key={cost.key} data-testid="zk-tile-cost" data-cost-key={cost.key} data-amount={cost.amount}>
              <span className="zk-tile__label">{t(`orders:process.invoice.cost.${cost.key}`)}</span>
              <span className="zk-tile__value">− {money(cost.amount)}</span>
              {cost.key === 'settlementCost' && <span className="zk-tile__meta">{t('orders:process.invoice.cost.settlementCostHint')}</span>}
            </div>
          )) : (
            <div className="zk-tile" data-testid="zk-tile-cost-hidden">
              <span className="zk-tile__label">{t('orders:process.invoice.cost.wzCost')}</span>
              <span className="zk-tile__value">
                {wzCostList.length
                  ? <span className="zk-source">{wzCostList.map((document) => (
                    <span key={document.id}>{document.wzNumber}: {document.costOfGoodsSold == null ? t('orders:process.delivery.costPending') : money(document.costOfGoodsSold)}</span>
                  ))}</span>
                  : marginVisibility.showCosts || caps.canViewWarehouseCosts ? '—' : <HiddenValue reason="costs" />}
              </span>
            </div>
          )}
          <div
            className={`zk-tile zk-tile--result${tileMargin.amount != null && tileMargin.amount < 0 ? ' zk-tile--loss' : ''}${margin.costState === 'KNOWN' ? '' : ' zk-tile--incomplete'}`}
            data-cost-state={margin.costState}
            data-testid="zk-invoice-margin"
            data-stage={margin.stage}
            data-amount={marginVisibility.showMargin && tileMargin.amount != null ? tileMargin.amount : undefined}
          >
            <span className="zk-tile__label">
              {t(ORDER_MARGIN_STAGE_LABEL_KEYS[margin.stage])}
              {marginVisibility.showMargin && margin.provisional && ` · ${t('orders:process.margin.provisionalShort')}`}
            </span>
            <span className="zk-tile__value">{marginVisibility.showMargin ? marginValue(tileMargin.amount, tileMargin.percent) : <HiddenValue reason="margin" />}</span>
            {marginVisibility.showMargin && !final && !invoiced && <span className="zk-tile__meta">{t('orders:process.invoice.marginBeforeApproval')}</span>}
            {marginVisibility.showMargin && margin.costIncomplete && <span className="zk-tile__meta" data-testid="zk-invoice-margin-incomplete">{incompleteText}</span>}
            {marginVisibility.showMargin && margin.provisional && <span className="zk-tile__meta">{t('orders:process.margin.provisional')}</span>}
          </div>
        </div>
        {breakdown?.costPending && !margin.costIncomplete && <p className="zk-margin-footer__hint">{t('orders:process.margin.incompleteHint')}</p>}
        {invoiced && marginVisibility.showMargin && margin.stage !== 'FINAL' && (
          <Callout tone="muted">{t('orders:process.invoice.finalForAdmin')}</Callout>
        )}
        {marginVisibility.showMargin && <p className="zk-margin-footer__hint zk-margin-footer__hint--muted">{t(`orders:process.margin.explain.${margin.stage}`)}</p>}
      </StepSection>

      {(marginVisibility.showMargin || caps.canViewCustomerPayments) && (
        <StepSection title={t('orders:process.invoice.managementTitle')}>
          <div className="zk-tiles">
            {caps.canViewCustomerPayments && (
              <div className="zk-tile">
                <span className="zk-tile__label">{t('orders:process.invoice.daysToPay')}</span>
                <span className="zk-tile__value">{daysToPay == null ? '—' : t('orders:process.invoice.days', { count: daysToPay })}</span>
              </div>
            )}
            {caps.canViewCustomerPayments && (
              <div className="zk-tile">
                <span className="zk-tile__label">{t('orders:process.invoice.paymentStatus')}</span>
                <span className="zk-tile__value">{t(`orders:customerPaymentStatus.${order.customerPaymentStatus || 'PENDING'}`)}</span>
                {dueDate && <span className="zk-tile__meta">{t('orders:process.invoice.dueOn', { date: formatDateEuropean(order.customerPaymentDueDate) })}</span>}
              </div>
            )}
            {marginVisibility.showMargin && (
              <div className="zk-tile">
                <span className="zk-tile__label">{t('orders:process.invoice.marginPerM2')}</span>
                <span className="zk-tile__value">
                  {caps.canViewMargin
                    ? (tileMargin.amount != null && m2 > 0 ? `${money(tileMargin.amount / m2)} / m²` : '—')
                    : <HiddenValue reason="adminOnly" />}
                </span>
              </div>
            )}
          </div>
        </StepSection>
      )}
    </div>
  );
}
